import { useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import { HOMEPAGE_APP_QR_URL } from '@lib/homepage/app-links';

const QR_CODE_SIZE_PX = 64;
// Most of the code on screen, not a sliver at the edge, counts as seen.
const VIEWED_THRESHOLD = 0.75;

type AppQrCodeProps = {
  placement: string;
  caption: string;
};

/**
 * A desktop-only QR code for the app. Phones never see it, since they can tap
 * the store badges, so the view event only ever counts desktop visitors.
 */
const AppQrCode = ({ placement, caption }: AppQrCodeProps) => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // The first callback fires on observe whatever the threshold, with
        // isIntersecting true for any overlap at all.
        if (!entry || entry.intersectionRatio < VIEWED_THRESHOLD) return;
        captureAnalyticsEvent(AnalyticsEvents.appQrViewed, { placement });
        observer.disconnect();
      },
      { threshold: VIEWED_THRESHOLD }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [placement]);

  return (
    <figure ref={ref} className="hidden items-center gap-3 md:flex">
      <span className="rounded-lg border border-landing-hairline bg-white p-1.5">
        <QRCodeSVG
          value={HOMEPAGE_APP_QR_URL}
          size={QR_CODE_SIZE_PX}
          aria-label="QR code linking to the app download page"
        />
      </span>
      <figcaption className="max-w-[9rem] font-open-sans text-xs leading-4 text-landing-slate">
        {caption}
      </figcaption>
    </figure>
  );
};

export default AppQrCode;
